import { Fragment, useMemo, useState } from 'react';
import { useCompetitionRegistrationListQuery } from '../../queries';
import { Registration } from '~/apis/ssf';
import { Table } from '~/components/Table/Table';
import { Link } from '~/components/Link/Link';
import { match } from 'ts-pattern';
import { Badge } from '~/components/Badge/Badge';
import { Heading } from '~/components/Heading/Heading';
import { take } from 'lodash';
import { ButtonLink } from '~/components/ButtonLink/ButtonLink';
import { Skeleton } from '~/components/Skeleton/Skeleton';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { formatPoints, teamScoreTotal } from '~/utils/points';

interface Props {
  competitionId: number;
  isTeamComp?: boolean;
}

interface TableProps {
  registrations: Registration[];
  isLoading?: boolean;
  title: string;
  isTeamComp?: boolean;
}

function RegistrationTable(props: TableProps) {
  const { title, registrations, isLoading, isTeamComp } = props;

  const [isExpanded, setIsExpanded] = useState(true);
  const list = isExpanded ? take(registrations, 5) : registrations;

  const totalResult = useMemo(() => teamScoreTotal(registrations), [registrations]);

  const totalCols = 5;

  return (
    <Table>
      <Table.Head>
        <Table.Row narrow>
          <Table.Col as="th" style={{ width: '30%' }}>
            Lyftare
          </Table.Col>
          <Table.Col as="th" style={{ width: '25%' }}>
            Förening
          </Table.Col>
          <Table.Col as="th" style={{ width: '75px' }}>
            Viktklass
          </Table.Col>
          <Table.Col as="th" style={{ width: '75px' }}>
            Resultat
          </Table.Col>
          <Table.Col as="th" style={{ width: '75px' }}>
            Kvalad
          </Table.Col>
        </Table.Row>
      </Table.Head>

      <Table.Body>
        {isLoading ? (
          <Table.Loading cols={totalCols} rows={5} />
        ) : (
          list.map((registration, index) => (
            <Table.Row key={`${title}-${index}`} narrow strikethrough={registration.state === 'WITHDRAWN'}>
              <Table.Col>
                <Link to={`/lifters/${registration.person.id}`}>
                  {registration.person.first_name} {registration.person.last_name}
                </Link>
                {registration.is_reserve && (
                  <Tooltip title="Reserv" as="span">
                    {' '}
                    (R)
                  </Tooltip>
                )}
              </Table.Col>
              <Table.Col>
                <Link to={`/clubs/${registration.person.club.id}`}>{registration.person.club.name}</Link>
              </Table.Col>
              <Table.Col>{registration.weight_class?.name ?? '-'}</Table.Col>
              <Table.Col>
                {registration.nomination_result ? formatPoints(registration.nomination_result) : '-'}
              </Table.Col>
              <Table.Col>
                {registration.weight_class === null ? (
                  <Tooltip title="Ingen kvalgräns" as="span">
                    <Badge variant="default" label="Ja" />
                  </Tooltip>
                ) : (
                  match(registration.is_qualified)
                    .with(true, () => <Badge variant="success" label="Ja" />)
                    .otherwise(() => <Badge variant="danger" label="Nej" />)
                )}
              </Table.Col>
            </Table.Row>
          ))
        )}

        {isTeamComp && (
          <Table.Row>
            <Table.Col colSpan={3} style={{ borderTop: '1px solid var(--color-fg-secondary)' }}>
              <span style={{ fontWeight: 'bold' }}>Total</span>
            </Table.Col>
            <Table.Col colSpan={totalCols - 3} style={{ borderTop: '1px solid var(--color-fg-secondary)' }}>
              <span style={{ fontWeight: 'bold' }}>{formatPoints(totalResult)}</span>
            </Table.Col>
          </Table.Row>
        )}
        {registrations.length > 4 && (
          <Table.Row>
            <Table.Col style={{ textAlign: 'center' }} colSpan={totalCols}>
              <ButtonLink onClick={() => setIsExpanded(p => !p)}>{isExpanded ? 'Visa fler' : 'Visa färre'}</ButtonLink>
            </Table.Col>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  );
}

export function CompetitionRegistrations(props: Props) {
  const { competitionId, isTeamComp } = props;

  const { data, isLoading } = useCompetitionRegistrationListQuery({
    competition: competitionId,
    ordering: '-nomination_result',
    page_size: 999,
  });

  const grouped = useMemo(() => {
    return (data?.results ?? []).reduce(
      (groups, entry) => {
        // Team competitions register lifters by team alone (no registration category), so group by
        // team rather than by tävlingstyp/ålderskategori/kön.
        const sex = entry.person?.is_male ? 'Herr' : 'Dam';
        const key = isTeamComp
          ? entry.team
            ? `${entry.team.division.name} — ${entry.team.name}`
            : 'Utan lag'
          : `${entry.competition_type?.description ?? '-'} - ${entry.age_category?.label ?? '-'} ${sex}`;

        // Create the array if it doesn't exist, then push
        (groups[key] ||= []).push(entry);

        return groups;
      },
      {} as Record<string, Registration[]>
    );
  }, [data, isTeamComp]);

  const orderedGroups = useMemo(() => {
    const entries = Object.entries(grouped);
    // For team competitions, surface the highest-scoring teams first.
    return isTeamComp ? entries.sort(([, a], [, b]) => teamScoreTotal(b) - teamScoreTotal(a)) : entries;
  }, [grouped, isTeamComp]);

  const totalWithdrawnCount = data?.results.filter(r => r.state === 'WITHDRAWN').length ?? 0;

  if (isLoading) {
    return (
      <div>
        <Skeleton style={{ height: '18px', width: '125px', margin: '16px 0' }} />
        <RegistrationTable registrations={[]} title={undefined!} isLoading={isLoading} isTeamComp={isTeamComp} />
      </div>
    );
  }

  return (
    <Fragment>
      <PageLayout.SubHeading>
        Anmälningar{' '}
        <span style={{ color: 'var(--color-text-lighter)', fontWeight: 'normal' }}>
          ({data?.results.length}st
          {totalWithdrawnCount > 0
            ? `, varav ${totalWithdrawnCount} ${totalWithdrawnCount > 1 ? 'strukna' : 'struken'}`
            : ''}
          )
        </span>
      </PageLayout.SubHeading>

      {orderedGroups.map(([title, registrations], index) => {
        const withdrawnCount = registrations.filter(r => r.state === 'WITHDRAWN').length ?? 0;

        return (
          <Fragment key={`g-${index}`}>
            <Heading as="h5">
              {title}{' '}
              <span style={{ color: 'var(--color-text-lighter)', fontWeight: 'normal' }}>
                ({registrations.length}st
                {withdrawnCount > 0 ? `, varav ${withdrawnCount} ${withdrawnCount > 1 ? 'strukna' : 'struken'}` : ''})
              </span>
            </Heading>
            <RegistrationTable registrations={registrations} title={title} isTeamComp={isTeamComp} />
          </Fragment>
        );
      })}
    </Fragment>
  );
}
